/**
 * load-error.ts — klasyfikacja błędu wczytania listy (prop `loadError` w `ConfigurableTable`).
 *
 * Lista, której zapytanie się nie powiodło, NIE może udawać pustej („Brak dokumentów… Dodaj
 * pierwszy.") — to wprowadza w błąd. Tabela pokazuje komunikat zależny od przyczyny; tu żyje
 * czyste mapowanie odpowiedzi serwera na jedną z czterech przyczyn:
 *
 *  - `badRequest`     — 400 / 404 / 422: zły adres listy (np. zły typ dokumentu lub firma
 *                       w linku). Ponowienie nic nie da — user ma otworzyć listę z menu.
 *  - `forbidden`      — 403: brak uprawnień do listy.
 *  - `moduleInactive` — 402: moduł listy nieaktywny (brak subskrypcji).
 *  - `unavailable`    — wszystko inne: 5xx (w tym 503), timeout / brak sieci (błąd axios bez
 *                       `response`), nieznany status, błąd spoza axios. JEDYNA przyczyna,
 *                       przy której ma sens „Spróbuj ponownie".
 *
 * 401 obsługuje interceptor klienta API (odnowienie sesji / wylogowanie), więc do listy
 * w praktyce nie dociera; gdyby dotarł — traktujemy go jak awarię (`unavailable`).
 *
 * Tu żyje też reguła AUTOMATYCZNEGO ponawiania zapytania listy (`listLoadRetry`) — ta sama
 * klasyfikacja decyduje, czy ponowienie ma sens.
 */

import { isAxiosError } from 'axios';

export type ListLoadErrorKind = 'badRequest' | 'forbidden' | 'moduleInactive' | 'unavailable';

/** Przyczyny, przy których przycisk „Spróbuj ponownie" ma sens (ponowienie może się udać). */
export function isRetryableListLoadError(kind: ListLoadErrorKind): boolean {
  return kind === 'unavailable';
}

/** Ile razy zapytanie listy ponawiamy automatycznie przy awarii — tyle co globalne `retry: 1`. */
const LIST_LOAD_AUTO_RETRIES = 1;

/**
 * Reguła automatycznego ponawiania zapytania LISTY — podpinana jako `retry: listLoadRetry`
 * w `useQuery` listy (sygnatura `retry` z TanStack Query: `failureCount` liczy nieudane próby
 * od 0). Przy awarii ponawia raz (jak globalne `retry: 1` z `lib/query.ts`); przy złym adresie,
 * braku uprawnień i nieaktywnym module — wcale: odpowiedź się nie zmieni, a komunikat pojawia
 * się od razu zamiast po opóźnieniu ponowienia.
 */
export function listLoadRetry(failureCount: number, error: unknown): boolean {
  return (
    failureCount < LIST_LOAD_AUTO_RETRIES && isRetryableListLoadError(classifyListLoadError(error))
  );
}

export function classifyListLoadError(err: unknown): ListLoadErrorKind {
  if (!isAxiosError(err) || !err.response) return 'unavailable';
  switch (err.response.status) {
    case 400:
    case 404:
    case 422:
      return 'badRequest';
    case 402:
      return 'moduleInactive';
    case 403:
      return 'forbidden';
    default:
      return 'unavailable';
  }
}
